import { useEffect, useMemo } from "react";
import { useSearchParams, useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Check, Shield, Star } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SearchWidget from "@/components/shared/SearchWidget";
import ProviderStrip from "@/components/shared/ProviderStrip";
import TermsHint from "@/components/shared/TermsHint";
import { BRAND } from "@/lib/data";

/**
 * Dynamic Keyword Insertion (DKI) landing page — designed for Google/Bing Ads.
 *
 * URL contract:
 *   /lp?kw={keyword}&city={optional}&category={optional}&sub={optional-subheadline}
 *
 * Google Ads macro: {keyword:car rental}   (default kicks in if the ad group has no keyword)
 * Bing Ads macro:   {keyword:car rental}   (same syntax)
 *
 * If NO `kw` param is present (direct hit without an ad), we redirect to the
 * homepage as the user requested — the LP has no reason to exist without the
 * dynamic hook.
 */
const MAX_KEYWORD_LEN = 60;
const MAX_SUB_LEN = 160;

// Strip HTML tags, control chars, and length-cap to prevent XSS + odd renderings
// when the ad platform feeds unusual characters into the URL.
function sanitize(raw, max) {
  if (!raw) return "";
  return String(raw)
    .replace(/<[^>]*>/g, " ")   // strip tags
    .replace(/[^\w\s\-&,.()'’/]/g, " ") // allow common natural-text chars
    .replace(/\s+/g, " ")       // collapse whitespace
    .trim()
    .slice(0, max);
}

// "miami car rental" → "Miami Car Rental" (leave existing caps alone so acronyms survive)
function titleCase(s) {
  return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

export default function Landing() {
  const [params] = useSearchParams();
  const { slug } = useParams();

  // Convert path slug like "miami-car-rental" into a natural phrase — falls
  // back if the caller only used a slug and no `kw` query param.
  const slugKeyword = slug ? slug.replace(/-/g, " ") : "";

  const rawKw = params.get("kw") || params.get("keyword") || slugKeyword;
  const rawSub = params.get("sub") || "";
  const cityParam = params.get("city") || "";
  const categoryParam = params.get("category") || "";

  const keyword = useMemo(() => sanitize(rawKw, MAX_KEYWORD_LEN), [rawKw]);
  const subCopy = useMemo(() => sanitize(rawSub, MAX_SUB_LEN), [rawSub]);
  const kwTitle = useMemo(() => titleCase(keyword), [keyword]);

  // Update document.title + meta description dynamically so paid landings
  // show the keyword in the browser tab and the shared-link preview.
  useEffect(() => {
    if (!keyword) return;
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = `${kwTitle} — Compare & Book | Carztohire`;
    const descText = subCopy ||
      `Compare ${keyword} deals from every major US brand. Free quote in under a minute — no booking fees.`;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
    }
    descTag.setAttribute("content", descText);
    return () => {
      document.title = prevTitle;
      if (prevDesc && descTag) descTag.setAttribute("content", prevDesc);
    };
  }, [keyword, kwTitle, subCopy]);

  // Redirect direct visitors (no ad keyword) to the homepage.
  if (!keyword) return <Navigate to="/" replace />;

  const headline = `Compare ${kwTitle} deals from every major US brand.`;
  const sub =
    subCopy ||
    `One inquiry. Every major provider compared. Zero booking fees. Prices for ${keyword} start from around $24/day.`;

  const widgetDefaults = {
    defaultCity: cityParam || "",
    defaultCategory: categoryParam || "",
  };

  return (
    <PageShell>
      {/* Hero */}
      <section
        className="relative bg-[#0B2545] text-white pt-10 md:pt-16 pb-6 md:pb-10 overflow-hidden"
        data-testid="lp-hero"
      >
        <div
          className="absolute inset-0 opacity-30 mix-blend-luminosity"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1553440569-bcc63803a83d?auto=format&fit=crop&w=1920&q=60)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2545]/60 via-[#0B2545]/80 to-[#0B2545]" />
        <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-4 flex items-center gap-3" data-testid="lp-overline">
            <span className="inline-block h-1.5 w-1.5 bg-white/85" />
            <span>{kwTitle} · US car rental aggregator</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-serif text-[9vw] md:text-[5.6vw] lg:text-[4.8vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl"
            data-testid="lp-headline"
          >
            {headline}
          </motion.h1>
          <p className="mt-5 md:mt-6 text-white/85 text-base md:text-xl max-w-3xl leading-relaxed" data-testid="lp-subheadline">
            {sub}
          </p>

          <div className="mt-6 md:mt-8 flex flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-inverse flex-1 md:flex-none justify-center" data-testid="lp-call-cta">
              <Phone className="h-4 w-4" strokeWidth={2} />
              <span className="hidden md:inline">Call {BRAND.phone}</span>
              <span className="md:hidden">Call</span>
            </a>
            <div className="relative inline-flex flex-col group flex-1 md:flex-none">
              <Link to="/contact" className="btn-ghost-dark w-full md:w-auto justify-center" data-testid="lp-quote-cta">
                <span className="hidden md:inline">Get a free quote</span>
                <span className="md:hidden">Get a quote</span>
                <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </Link>
              <TermsHint variant="dark" align="right" dataTestId="lp-quote-hint" />
            </div>
          </div>

          {/* Search widget — pre-filled from URL params */}
          <div className="mt-8 md:mt-10" data-testid="lp-widget-wrap">
            <SearchWidget {...widgetDefaults} />
          </div>
        </div>
      </section>

      <ProviderStrip />

      {/* 3 trust signals */}
      <section className="py-8 md:py-14 bg-[#F5F7FA]" data-testid="lp-trust">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline mb-6 md:mb-8">Why compare through Carztohire</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TRUST_SIGNALS.map((t, i) => (
              <div
                key={i}
                className="bg-white border border-line p-6 md:p-8"
                data-testid={`lp-trust-${i + 1}`}
              >
                <div className="inline-flex items-center justify-center h-10 w-10 md:h-12 md:w-12 border border-[#0B2545] text-[#0B2545] mb-4 md:mb-5">
                  <t.icon className="h-4 w-4 md:h-5 md:w-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-[#0B2545] leading-tight mb-2">
                  {t.title}
                </h3>
                <p className="text-sm md:text-base text-[#5B6472] leading-relaxed">
                  {t.body.replace("{kw}", keyword)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 md:py-16 bg-[#0B2545] text-white" data-testid="lp-final-cta">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="md:col-span-8">
              <div className="overline-light mb-3 md:mb-4">Ready when you are</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                Call now for a {kwTitle} quote — every major provider compared in minutes.
              </h2>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <a href={BRAND.phoneHref} className="btn-inverse justify-center" data-testid="lp-final-call">
                <Phone className="h-4 w-4" strokeWidth={2} />
                {BRAND.phone}
              </a>
              <div className="relative inline-flex flex-col group">
                <Link to="/contact" className="btn-ghost-dark justify-center" data-testid="lp-final-quote">
                  Submit an inquiry
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </Link>
                <TermsHint variant="dark" align="center" dataTestId="lp-final-hint" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

const TRUST_SIGNALS = [
  {
    icon: Check,
    title: "Zero booking fees",
    body: "Compensated by provider partners — never by you. The {kw} quote you see is the price you pay.",
  },
  {
    icon: Shield,
    title: "Real human support",
    body: "Talk to a US-based team, not a chatbot. Mon-Sun 7am-11pm MST.",
  },
  {
    icon: Star,
    title: "Every major brand",
    body: "Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, Thrifty — compared for {kw} side-by-side.",
  },
];
